import { useState, useEffect, useRef, useCallback } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import toast from 'react-hot-toast'
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/solid'
import { apiRequest } from '../lib/apiClient'
import { useActivation } from '../hooks/useActivation'
import { useAuth } from '../hooks/useAuth'
import { useDebounceScan } from '../hooks/useDebounceScan'

const activationApiMessages = {
  200: 'Bateria ativada com sucesso!',
  401: 'Nao autorizado. Verifique sua sessao.',
  404: 'Bateria nao encontrada.',
  409: 'Esta bateria ja foi ativada anteriormente.',
  400: 'Dados invalidos para ativacao.',
  408: 'Tempo limite na ativacao. Sem confirmacao de heartbeat.',
  422: 'Bateria nao detectada no gabinete. Verifique a insercao.',
  default: 'Erro na ativacao. Tente novamente.',
}

function StatusOverlay({ status, message, onRetry }) {
  const bgColor = {
    SUCCESS: 'bg-green-500/90',
    ERROR: 'bg-red-500/90',
    ACTIVATING: 'bg-black/80',
  }[status]

  const icon = {
    SUCCESS: <CheckCircleIcon className="w-24 h-24 text-white" />,
    ERROR: <XCircleIcon className="w-24 h-24 text-white" />,
    ACTIVATING: <ArrowPathIcon className="w-24 h-24 text-yellow-500 animate-spin" />,
  }[status]

  return (
    <div className={`absolute inset-0 ${bgColor} flex flex-col justify-center items-center p-4 z-10 rounded-lg`}>
      {icon}
      <p className="text-center text-white font-bold text-2xl mt-4">{message}</p>
      {status === 'ERROR' && onRetry && (
        <button
          onClick={onRetry}
          className="mt-6 bg-blue-600 text-white font-bold py-3 px-6 rounded-md hover:bg-blue-500 transition-colors"
        >
          Tentar Novamente
        </button>
      )}
    </div>
  )
}

function ActivationPage() {
  const { cabinetSerial, slotPosition, clearCabinetData } = useActivation()
  const { setAccessIssue } = useAuth()
  const { checkAndScan, clearDebounce } = useDebounceScan(3000)
  const [activationStatus, setActivationStatus] = useState('SCANNING_BATTERY')
  const [batteryId, setBatteryId] = useState(null)
  const [cameraError, setCameraError] = useState(null)
  const [lastApiMessage, setLastApiMessage] = useState('')

  const qrScannerRef = useRef(null)
  const appStateRef = useRef({ activationStatus })

  useEffect(() => {
    appStateRef.current = { activationStatus }
  }, [activationStatus])

  const resetBatteryFlow = useCallback(() => {
    setBatteryId(null)
    setLastApiMessage('')
    setActivationStatus('SCANNING_BATTERY')
    
    // Resume scanner if it was paused
    if (qrScannerRef.current && qrScannerRef.current.getState() === 3) {
      try {
        qrScannerRef.current.resume()
      } catch (err) {
        console.warn('Could not resume scanner:', err)
      }
    }
  }, [])

  const triggerActivation = useCallback(
    async (currentBatteryId) => {
      setActivationStatus('ACTIVATING')
      
      // Pause scanner while request is flying
      if (qrScannerRef.current && qrScannerRef.current.getState() === 2) { // 2 = SCANNING
        try {
          qrScannerRef.current.pause(true)
        } catch (err) {
          console.warn('Could not pause scanner:', err)
        }
      }

      if (!cabinetSerial || !Number.isInteger(slotPosition)) {
        setLastApiMessage('Gabinete nao configurado corretamente.')
        setActivationStatus('ERROR')
        clearDebounce()
        return
      }

      const payload = {
        internal_id: currentBatteryId,
        cabinet_serial: cabinetSerial,
        slot_position: Number(slotPosition),
      }

      try {
        await apiRequest('/battery-activator', {
          method: 'POST',
          body: JSON.stringify(payload),
          handleAuthErrorManually: true,
        })

        // Logica de sucesso imediato usando overlay de Toast com design do Tailwind
        toast.success(activationApiMessages[200], {
          duration: 3000,
          position: 'top-center',
          style: {
            background: '#22c55e',
            color: '#fff',
            fontWeight: 'bold',
            padding: '16px',
            fontSize: '18px',
          },
        })

        // Retorna imediatamente para escaneamento, nao encobre a tela com "SUCCESS"
        resetBatteryFlow()
      } catch (error) {
        if (error.message === 'Missing auth token') {
          clearDebounce()
          setAccessIssue('session')
          return
        }

        const statusMatch = error.message.match(/status: (\d+)/)
        const statusCode = statusMatch ? parseInt(statusMatch[1], 10) : 500

        // Auth problems get the dedicated guided screen (with "Entrar novamente"),
        // not the generic retry overlay that just re-fires the same failing call.
        if (statusCode === 401) {
          clearDebounce()
          setAccessIssue('session')
          return
        }
        if (statusCode === 403) {
          clearDebounce()
          setAccessIssue('permission')
          return
        }

        // Idempotency check: if Already Activated, treat as Success for workflow continuity.
        if (statusCode === 409) {
          toast.success('Bateria ja ativada (Recuperado).', {
            duration: 3000,
            position: 'top-center',
            style: {
              background: '#22c55e',
              color: '#fff',
              fontWeight: 'bold',
              padding: '16px',
              fontSize: '18px',
            },
          })
          resetBatteryFlow()
          return
        }

        const message = activationApiMessages[statusCode] || activationApiMessages.default

        setLastApiMessage(message)
        setActivationStatus('ERROR')
        clearDebounce()
      }
    },
    [cabinetSerial, slotPosition, resetBatteryFlow, clearDebounce, setAccessIssue],
  )

  const isScannerRunningRef = useRef(false)

  useEffect(() => {
    let isMounted = true
    const qrScanner = new Html5Qrcode('battery-qr-reader', { verbose: false })
    qrScannerRef.current = qrScanner

    const startScanner = async () => {
      try {
        await Html5Qrcode.getCameras()
        if (!isMounted || !qrScannerRef.current) return

        await qrScanner.start(
          { facingMode: 'environment' },
          { 
              fps: 5, 
              qrbox: { width: 250, height: 250 },
              aspectRatio: 1.0 
          },
          (decodedText) => {
            if (appStateRef.current.activationStatus !== 'SCANNING_BATTERY' || !isMounted) return

            checkAndScan(decodedText, (text) => {
              const newBatteryId = text.trim()
              if (!newBatteryId) {
                toast.error('ID da bateria invalido.')
                return
              }

              setBatteryId(newBatteryId)
              triggerActivation(newBatteryId)
            })
          },
          () => {},
        )

        if (isMounted) {
            isScannerRunningRef.current = true
        }
      } catch (error) {
        if (!isMounted) return
        const message =
          error.name === 'NotAllowedError' ? 'Permissao da camera negada.' : error.message
        setCameraError(message)
      }
    }

    startScanner()

    return () => {
        isMounted = false
        const scanner = qrScannerRef.current
        
        if (scanner) {
          qrScannerRef.current = null
          
          try {
             if (isScannerRunningRef.current) {
               scanner.stop()
                 .catch(err => console.warn('Scanner stop warning:', err))
                 .finally(() => {
                   scanner.clear().catch(err => console.warn('Scanner clear warning:', err))
                 })
             } else {
               scanner.clear().catch(err => console.warn('Scanner clear warning:', err))
             }
          } catch (e) {
              console.warn("Erro ao parar o scanner durante a desmontagem", e);
          }
          
          isScannerRunningRef.current = false
        }
    }
  }, [triggerActivation])

  const getHeaderText = () => {
    if (activationStatus === 'SUCCESS') return 'Ativacao concluida!'
    if (activationStatus === 'ERROR') return 'Falha na ativacao'
    return `Conectado: Serial ${cabinetSerial} - Slot ${slotPosition}`
  }

  const getInstructionText = () => {
    const instructions = {
      SCANNING_BATTERY: 'Escaneie a Bateria',
      ACTIVATING: 'Ativando... Aguarde!',
      SUCCESS: 'Pronto para a proxima bateria.',
      ERROR: lastApiMessage,
    }

    return instructions[activationStatus] || ''
  }

  const handleRetry = () => {
    // User requested to just clear the error screen and allow rescanning, 
    // instead of retrying the API call immediately with the same ID.
    toast.dismiss()
    clearDebounce()
    resetBatteryFlow()
  }

  const isScanning = activationStatus === 'SCANNING_BATTERY'
  const showOverlay = !isScanning && !cameraError

  return (
    <div className="min-h-screen bg-[#0d1117] text-white flex flex-col items-center justify-center p-4 font-sans">
      <div className="w-full max-w-md mx-auto">
        <header className="text-center mb-6">
          <h1 className="text-lg text-gray-300 font-semibold">{getHeaderText()}</h1>
          <p className="text-xl font-bold tracking-wider text-gray-100 h-7">{getInstructionText()}</p>
          <button
            onClick={clearCabinetData}
            className="mt-4 bg-gray-700/80 text-white text-sm font-semibold py-2 px-4 rounded-md hover:bg-gray-600 transition-colors"
          >
            Change Cabinet
          </button>
        </header>

        <main>
          <div className="relative w-full aspect-square bg-black rounded-lg border-2 border-gray-700 flex items-center justify-center overflow-hidden">
            <div id="battery-qr-reader" className="w-full h-full [&>video]:object-cover [&>video]:w-full [&>video]:h-full" />

            {cameraError && <StatusOverlay status="ERROR" message={cameraError} />}

            {showOverlay && (
              <StatusOverlay
                status={activationStatus}
                message={getInstructionText()}
                onRetry={handleRetry}
              />
            )}

            {isScanning && !cameraError && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="w-[250px] h-[250px]"
                  style={{ boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)' }}
                >
                  <div className="relative w-full h-full border-2 border-green-400/70 rounded-lg" />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default ActivationPage
